/**
 * Application entry point.
 *
 * Boots the game shell, installs the debug API, and — when `?bench=` is present — pins a
 * deterministic scene instead of starting a shift, so the screenshot harness always
 * captures the same frame.
 */

import { Game, FIXED_DT } from './app/game';
import { bootTarget } from './app/bootProgress';
import { createStallDetector, recoveryTier, recoveryAttempt, recoveryUrl } from './app/bootRecovery';
import { sendBootStall, sendVisit } from './app/telemetry';
import { registerServiceWorker, wireAudioResume } from './app/pwa';
import type { QualityTier } from './render/world';
import type { WeatherPresetName } from './sim/weather';
import { installDebugApi, installGameDebugApi } from './debug/api';
import { applyBenchmark, isBenchmarkName, BENCHMARKS } from './debug/benchmarks';
import { createKrakenState, type KrakenPhase } from './sim/kraken';
import { krakenEyePoint, krakenStareHeading } from './content/creatureScale';
import { KRAKEN } from './content/boss';
import { mountEnvironmentPanel } from './debug/environmentPanel';
import { mountFlycam } from './debug/flycam';
import './debug/flycam.css';
import './debug/environmentPanel.css';

export { FIXED_DT };

async function boot(): Promise<void> {
  const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
  const bootEl = document.getElementById('boot');
  const bootFill = document.getElementById('bootFill');
  const bootMsg = document.getElementById('bootMsg');

  /*
   * The loading bar, rewritten because the old one lied twice over.
   *
   * It had three steps — 15% before the engine was built, 65% immediately after, then
   * 100% — and the 65% was set BEFORE the slow part even began. Everything expensive
   * (the rig's geometry, the scans, shader compilation) happens inside the wait that
   * follows, so the bar parked at 65% for the entire real load and then snapped to done.
   * It also narrated itself in engine jargon: "compiling shaders" tells a player nothing
   * about whether their game is stuck or nearly ready.
   *
   * What replaces it tracks Babylon's own queue of outstanding assets, and says what is
   * happening in words that describe the world rather than the renderer.
   */
  /*
   * Progress is raised, never set: the inline script in index.html owns the animation
   * and eases toward whatever target it has been handed. Calling it a target rather
   * than a value is the point — this code knows roughly how far along the load is, and
   * the easing turns that into something that always appears to be moving.
   */
  const setProgress = (pct: number, msg?: string): void => {
    window.__boot?.(pct, msg);
    // Fallback for any host without the inline script (a bare test harness, say).
    if (!window.__boot) {
      if (bootFill) bootFill.style.width = `${Math.min(100, Math.round(pct))}%`;
      if (bootMsg && msg) bootMsg.textContent = msg;
    }
  };

  /*
   * Phase wording lives with the animation, in index.html, because it is derived from
   * the bar's DRAWN position rather than from the target this file sets — otherwise the
   * text races ahead of the fill and the two contradict each other.
   */

  const params = new URLSearchParams(window.location.search);
  const quality = (params.get('quality') as QualityTier | null) ?? undefined;
  const bench = params.get('bench');
  const isBench = bench !== null;
  // Inspection mode: free camera, no simulation, no HUD.
  const isFly = params.has('fly');

  setProgress(38);

  const game = new Game({
    canvas,
    ...(quality ? { quality } : {}),
    // A benchmark run wants a pinned camera and a frozen clock, not a live shift.
    autoStart: !isBench && !isFly && params.has('play'),
  });

  const world = game.world;

  /*
   * Real progress, from here to ready.
   *
   * `getWaitingItemsCount` is Babylon's own count of assets still outstanding, so the
   * bar now moves because the game is actually loading rather than because the code
   * reached a particular line. The count is remembered at its peak and read as a
   * fraction of that, since it starts at zero, climbs as requests are queued, and falls
   * as they land — taking the maximum is what turns that shape into a monotonic ratio.
   *
   * One guard, not two: the displayed value never goes backwards. There used to be a
   * second — a creep of a fraction of a percent per tick regardless of the truth, on the
   * reasoning that a stationary bar reads as a hung game. It bought that liveness with
   * the bar's only real job. See bootProgress.ts; both inputs move continuously on their
   * own, so the motion survives without anything being invented.
   */
  /*
   * The intro film is part of the load, not something that trickles in behind it.
   *
   * It is the single largest asset the title screen needs and it used to stream itself
   * while the gate was already up, so the first click landed on a frozen frame. It is now
   * fetched in full here and the boot overlay waits on it, which also means it deserves a
   * real share of the bar rather than a silent wait at the end of one.
   *
   * Skipped for the entry points that never show the gate: a benchmark must not spend a
   * cold cache on a film it hides, and `?play` and `?fly` go straight past the title.
   */
  const wantsFilm = !isBench && !isFly && !params.has('play');
  let filmFraction = wantsFilm ? 0 : 1;
  const filmLoaded = wantsFilm
    ? game.screens.loadIntroFilm((f) => { filmFraction = f; })
    : Promise.resolve();

  /*
   * A boot that cannot finish must not hang at "almost ready" — that was launch day.
   * On machines whose driver dies under the initial shader compile, the scene never
   * becomes ready, the await below never resolves, and there was no timeout: the bar
   * sat one band from the top forever. The detector (bootRecovery.ts) judges the boot
   * stuck — downloads done but the scene still not ready, or a WebGL context loss that
   * restoration does not cure — and reloads once at the next quality tier down, which
   * removes most of the shader work. Benchmarks and the flycam are exempt: a capture
   * harness must never navigate itself to a different quality than it was asked for.
   */
  const bootStartedAt = Date.now();
  const detector = createStallDetector();
  const watchStall = !isBench && !isFly;
  if (watchStall) {
    canvas.addEventListener('webglcontextlost', () => detector.noteContextLost(Date.now()));
  }

  const recoverFromStall = (): void => {
    window.clearInterval(tracker);
    const tried = quality ?? 'high';
    sendBootStall(tried, recoveryAttempt(quality), (Date.now() - bootStartedAt) / 1000);
    const next = recoveryTier(quality);
    if (next) {
      window.location.replace(recoveryUrl(window.location.href, next));
    } else {
      // Out of rungs. Driving the bar to 100 is what lets index.html show the override
      // label at all — its phase text owns the message until the bar is drawn full.
      setProgress(100, 'could not build the scene — your graphics driver gave out; reload to retry');
    }
  };

  let peakWaiting = 0;
  let shown = 38;
  const tracker = window.setInterval(() => {
    const waiting = world.scene.getWaitingItemsCount();
    peakWaiting = Math.max(peakWaiting, waiting);
    const settled = peakWaiting === 0 ? 0 : 1 - waiting / peakWaiting;
    /*
     * Monotonic, and never ahead of the truth. This used to carry an unconditional
     * `shown + 0.25` per tick, which walked the bar to the top in about twenty seconds
     * regardless of what had actually loaded — so a slow connection reached "almost
     * ready" on a timer and then sat there for the whole real download. See
     * bootProgress.ts. Both inputs move continuously by themselves and index.html eases
     * toward whatever it is given, so nothing needs to be invented to keep it alive.
     */
    shown = Math.max(shown, bootTarget(settled, filmFraction));
    setProgress(shown);

    if (watchStall) {
      if (settled >= 1 && filmFraction >= 1) detector.noteWorkSettled(Date.now());
      if (detector.poll(Date.now())) recoverFromStall();
    }
  }, 90);

  // --- Clock control for tools and tests ------------------------------------------------
  let frozen = false;
  const frameCallbacks = new Set<() => void>();

  const host = {
    world,
    freeze: () => { frozen = true; game.setPaused(true, true); },
    unfreeze: () => { frozen = false; game.setPaused(false); },
    isFrozen: () => frozen,
    setTime: (t: number) => {
      frozen = true;
      game.setPaused(true, true);
      world.time = t;
      game.sim.time = t;
      world.update(0);
      // Reconcile vessels and effects by hand: a pinned, paused capture never reaches
      // the frame loop's sync step.
      game.syncForCapture();
    },
    stepTicks: (n: number) => {
      for (let i = 0; i < n; i++) {
        game.sim.tick(FIXED_DT);
        world.update(FIXED_DT);
      }
    },
    setSimWeather: (preset: WeatherPresetName) => {
      game.sim.setWeatherPreset(preset, 0);
    },
    setRigIntegrity: (integrity: number) => {
      game.sim.resources.integrity = Math.max(0, Math.min(100, integrity));
    },
    placeVessel: (
      kind: string,
      bearingDeg: number,
      rangeKm: number,
      headingDeg?: number,
      submerged?: boolean,
      sinkProgress?: number,
    ) => {
      const time = bench && isBenchmarkName(bench) ? BENCHMARKS[bench].time : 0;
      game.placeVesselForCapture(
        kind,
        bearingDeg,
        rangeKm,
        time,
        headingDeg,
        submerged,
        sinkProgress,
      );
    },
    setKrakenPhase: (phase: string, timer: number, submergence?: number) => {
      /*
       * Stage the finale's phase machine for capture. The heading snap matters: the
       * rise presents the eye by facing the flank to the platform, and a staged frame
       * cannot wait for the machine's own slow turn to get there.
       */
      const sim = game.sim;
      if (!sim.kraken) sim.kraken = createKrakenState();
      sim.kraken.phase = phase as KrakenPhase;
      sim.kraken.timer = timer;
      const boss = sim.vessels.find((v) => v.kind === 'kraken');
      // Stage a chosen instant of the dive; the ramp is a running animation a frozen
      // capture has no seconds to spend on.
      if (boss && submergence !== undefined) boss.submergence = submergence;
      if (boss && (phase === 'rise' || phase === 'dying')) {
        boss.heading = krakenStareHeading(boss.pos.x, boss.pos.z);
        boss.submerged = false;
        boss.submergedDepth = 0;
        if (phase === 'rise') {
          const progress = Math.min(1, timer / KRAKEN.riseSeconds);
          const eye = krakenEyePoint(
            boss.pos.x, boss.pos.y, boss.pos.z, boss.heading, progress,
          );
          boss.eyeTarget = { x: eye.x, y: eye.y, z: eye.z, r: KRAKEN.eyeTargetRadius };
        }
      }
    },
    onFrame: (cb: () => void) => {
      frameCallbacks.add(cb);
      return () => frameCallbacks.delete(cb);
    },
  };

  const api = installDebugApi(host);
  installGameDebugApi(api, game);
  let disposeEnvironmentPanel: (() => void) | null = null;
  let disposeFlycam: (() => void) | null = null;

  if (isFly) {
    /*
     * Free camera. The simulation is paused and the game's own look controls never get a
     * chance to fight for the camera, so this cannot leak into a real session.
     *
     * `&bench=<name>` still works alongside it, which is how you fly around a staged
     * scene — vessels, creatures, weather and all — rather than an empty sea.
     */
    game.hud.hide();
    game.screens.hide();
    game.setPaused(true, true);
    disposeFlycam = mountFlycam({ world, canvas, onFrame: host.onFrame });
  } else if (isBench) {
    // Benchmarks render the scene alone: no HUD chrome over the visual contract shots.
    game.hud.hide();
    game.screens.hide();
  } else if (params.has('debug')) {
    // Only mounted on request: its collapsed button sits over the funds readout, and a
    // player should never see it.
    disposeEnvironmentPanel = mountEnvironmentPanel(api, { initiallyOpen: true });
  }

  /*
   * Offline support, and audio that survives the phone locking.
   *
   * Skipped entirely for benchmark runs: a cached shell would make deterministic capture
   * depend on what happened to be in a cache, which is the one thing those runs cannot
   * tolerate.
   */
  if (!isBench) {
    registerServiceWorker({
      onUpdateReady: (apply) => game.screens.showUpdatePrompt(apply),
    });
    wireAudioResume(() => game.audio.resume());
  }

  game.start();
  world.engine.onEndFrameObservable.add(() => {
    if (frameCallbacks.size > 0) for (const cb of Array.from(frameCallbacks)) cb();
  });

  window.addEventListener('beforeunload', () => {
    disposeEnvironmentPanel?.();
    disposeFlycam?.();
  });

  // The gate is already rendered underneath this overlay, so nothing is shown to the
  // player until both of these are in: the scene it will sit over, and the film behind it.
  await Promise.all([world.scene.whenReadyAsync(), filmLoaded]);
  window.clearInterval(tracker);
  setProgress(100, 'ready — take the watch');

  // The ad-block-proof page view: this load built the scene and reached the gate.
  // Same exemptions as the stall watchdog — captures and the flycam are not visits.
  if (watchStall) sendVisit(quality ?? 'high', (Date.now() - bootStartedAt) / 1000);

  /*
   * Let the bar finish before the overlay goes.
   *
   * On a warm cache the whole load lands in under four seconds, which is faster than
   * the fill can travel — so without this the player watches the loading screen vanish
   * at forty per cent, which reads worse than a slow load. Capped, so a browser that
   * never runs the inline script cannot hold the game hostage.
   */
  const settledBy = Date.now() + 700;
  while (window.__boot && !window.__bootSettled && Date.now() < settledBy) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }

  if (bench && isBenchmarkName(bench)) {
    applyBenchmark(world, host, bench);
  }

  await api.waitFrames(2);
  api.ready = true;

  bootEl?.classList.add('hidden');
  setTimeout(() => bootEl?.remove(), 700);
}

boot().catch((err: unknown) => {
  console.error('[deepwater] boot failed', err);
  const msg = document.getElementById('bootMsg');
  if (msg) {
    msg.textContent = 'could not start — see the browser console';
    msg.style.color = '#d05a4a';
  }
});
